import { useCallback, useEffect, useMemo, type MouseEvent } from 'react';
import { Box, Divider, Grid2 as Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EL_COLORS, color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import {
  useAppOutputMutations,
  useCreateWorkflowAppMutation,
  useWorkflowAppByWorkflowIdQuery,
} from '@/queries/useWorkflowApp';
import { useWorkflowStore } from '@/state/workflow.state';
import { TextSmMd, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { FileIcon } from '@/UI/Icons/FileIcon';
import { MinusIcon } from '@/UI/Icons';
import { PlusIcon } from '@/UI/Icons/PlusIcon';
import { FlexCenVerSpaceBetween, FlexCol } from '@/UI/styles';
import { useNodes } from '../FlowContext';
import type { WorkflowAppDraftOverrides } from '@/types/workflow-app.types';

const EMPTY_DRAFT_OVERRIDES: WorkflowAppDraftOverrides = {
  inputs: [],
  outputs: [],
};

function AppBuilderPanelTitleRow() {
  const workflowName = useWorkflowStore((state) => state.recipe.name);

  return (
    <FlexCenVerSpaceBetween
      data-testid="app-builder-title-row"
      sx={{
        px: 2,
        py: 1.5,
        gap: 1,
        minHeight: 40,
        width: '100%',
      }}
    >
      <TextSmMd component="span" color={color.White100} noWrap sx={{ flex: 1, minWidth: 0 }}>
        {workflowName}
      </TextSmMd>
      <Box sx={{ flexShrink: 0, display: 'flex', color: color.White100 }} aria-hidden>
        <FileIcon width={17} height={17} />
      </Box>
    </FlexCenVerSpaceBetween>
  );
}

function AppBuilderPanelSectionTitle({ children }: { children: string }) {
  return (
    <Box sx={{ px: 2, py: 2 }}>
      <TextSmMd color={color.White64_T}>{children}</TextSmMd>
    </Box>
  );
}

function AppBuilderOutputRow({
  label,
  onRemove,
  removeDisabled,
}: {
  label?: string;
  onRemove: () => void;
  removeDisabled: boolean;
}) {
  const { t } = useTranslation();

  const handleRemove = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onRemove();
    },
    [onRemove],
  );

  if (!label) {
    return null;
  }

  return (
    <FlexCenVerSpaceBetween sx={{ px: 2, width: '100%' }}>
      <TextSmRg color={color.White100} noWrap sx={{ flex: 1 }}>
        {label}
      </TextSmRg>
      <AppIconButton
        type="button"
        aria-label={t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_CANVAS.REMOVE_OUTPUT_ARIA)}
        disabled={removeDisabled}
        onClick={handleRemove}
      >
        <MinusIcon width={16} height={16} />
      </AppIconButton>
    </FlexCenVerSpaceBetween>
  );
}

export function AppBuilderPanel() {
  const { t } = useTranslation();
  const workflowId = useWorkflowStore((state) => state.recipe.id);
  const workflowNodes = useNodes();
  const { data: workflowApp, isLoading } = useWorkflowAppByWorkflowIdQuery(workflowId);
  const {
    mutate: createWorkflowApp,
    data: createdWorkflowApp,
    isPending: isCreatingWorkflowApp,
    isError: isCreateWorkflowAppError,
  } = useCreateWorkflowAppMutation();

  useEffect(() => {
    if (!workflowId || isLoading || isCreatingWorkflowApp || workflowApp !== null || isCreateWorkflowAppError) {
      return;
    }
    createWorkflowApp({ workflowId });
  }, [isCreateWorkflowAppError, createWorkflowApp, isLoading, isCreatingWorkflowApp, workflowApp, workflowId]);

  const resolvedCreatedWorkflowApp = createdWorkflowApp?.workflowId === workflowId ? createdWorkflowApp : null;
  const resolvedWorkflowApp = workflowApp ?? resolvedCreatedWorkflowApp;
  const draftOverrides = resolvedWorkflowApp?.draftOverrides ?? EMPTY_DRAFT_OVERRIDES;
  const { removeOutput, isUpdatingOutput } = useAppOutputMutations(resolvedWorkflowApp);

  const workflowNodeNames = useMemo(() => {
    return new Map(workflowNodes.map((node) => [node.id, node.data.name ?? node.id]));
  }, [workflowNodes]);

  const sortedOutputs = useMemo(() => {
    return [...draftOverrides.outputs].sort((a, b) => a.order - b.order);
  }, [draftOverrides.outputs]);

  const sortedInputs = useMemo(() => {
    return [...draftOverrides.inputs].sort((a, b) => a.order - b.order);
  }, [draftOverrides.inputs]);

  const getNodeDisplayName = (nodeId: string) => {
    return workflowNodeNames.get(nodeId);
  };

  return (
    <Grid size="grow" sx={{ overflowY: 'auto', display: 'flex', flexDirection: 'column' }} className="wea-no-scrollbar">
      <AppBuilderPanelTitleRow />
      <Divider sx={{ borderColor: EL_COLORS.BoxBorder }} />

      <AppBuilderPanelSectionTitle>
        {t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_RIGHT_PANEL.OUTPUTS)}
      </AppBuilderPanelSectionTitle>

      <FlexCol
        data-testid="app-builder-outputs"
        sx={{
          alignItems: 'flex-start',
          pb: 2,
        }}
      >
        {sortedOutputs.map((output) => (
          <AppBuilderOutputRow
            key={output.nodeId}
            label={getNodeDisplayName(output.nodeId)}
            onRemove={() => removeOutput(output.nodeId)}
            removeDisabled={!resolvedWorkflowApp || isUpdatingOutput}
          />
        ))}

        {sortedOutputs.length === 0 && (
          <FlexCol
            data-testid="app-builder-empty-state"
            sx={{
              alignItems: 'flex-start',
              px: 2,
              pt: 2,
              pb: 1,
              gap: 0.5,
            }}
          >
            <TextSmRg color={color.White64_T}>
              {t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_RIGHT_PANEL.EMPTY_NO_OUTPUTS_LINE_1)}
              <br />
              {t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_RIGHT_PANEL.EMPTY_NO_OUTPUTS_LINE_2)}
            </TextSmRg>
          </FlexCol>
        )}
        <Box sx={{ px: 2, py: 2 }}>
          <ButtonContained
            mode="text"
            size="small"
            type="button"
            data-testid="app-builder-add-output"
            startIcon={<PlusIcon width={16} height={16} />}
          >
            {t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_RIGHT_PANEL.ADD_OUTPUT)}
          </ButtonContained>
        </Box>
      </FlexCol>

      {sortedInputs.length > 0 && (
        <>
          <Divider sx={{ mt: 1.5, borderColor: EL_COLORS.BoxBorder }} />
          <AppBuilderPanelSectionTitle>
            {t(I18N_KEYS.RECIPE_MAIN.FLOW.APP_BUILDER_RIGHT_PANEL.INPUTS)}
          </AppBuilderPanelSectionTitle>
          <FlexCol data-testid="app-builder-inputs">{/* TODO(taylor): Render inputs here */}</FlexCol>
        </>
      )}
    </Grid>
  );
}
